import dayjs from 'dayjs';

// Fase E.2 -- funcoes PURAS de apresentacao das condicoes comerciais da
// inscricao no Portal do Responsavel ("quanto ficou minha participacao e
// quais foram as condicoes", NUNCA "como eu pago" -- isso fica para uma
// fase futura E.3). Extraidas em modulo separado do mesmo jeito que
// `eventos/cobrancasEventosAcoes.js` -- o projeto nao tem infraestrutura
// de teste de componente React (sem @testing-library/react instalado),
// entao toda a logica de formatacao/decisao precisa viver em funcoes
// puras testaveis via Jest puro, nunca inline no JSX.
//
// Fonte da verdade e' SEMPRE o que o backend ja envia (valor_bruto/
// desconto_reais/valor_final/quantidade_parcelas/parcelas) -- nada aqui
// soma parcelas, recalcula valor_final, divide valor por quantidade, ou
// infere vencimento. O backend ja normaliza quando as condicoes NAO
// devem ser apresentadas como vigentes (SOLICITADA/REJEITADA/CANCELADA
// -- ver evento_inscricao_portal_service.listar_minhas_inscricoes),
// mas o frontend AINDA assim verifica a presenca dos campos antes de
// renderizar (defesa em profundidade, mesmo padrao ja usado em
// `parcelaElegivelParaWhatsApp`/`grupoElegivelParaWhatsApp`).

// Labels fixos usados pelo card (MinhasInscricoesList.js) -- centralizados
// aqui (em vez de string literal inline no JSX) para que a linguagem seja
// testavel: E.2 fala SOMENTE sobre "quanto"/"quais condicoes", nunca sobre
// "como pagar" -- nunca "pendente"/"em aberto"/"pagar"/"boleto", mesmo numa
// inscricao EFETIVADA cuja participacao foi cancelada depois (ver
// docstring de `inscricaoTemCondicoesComerciais`/investigacao E.2, secao
// "PARTICIPACAO CANCELADA"). Cobranca/boleto/status de pagamento ficam
// para uma fase futura E.3, secao propria.
export const LABEL_CONDICOES_COMERCIAIS = 'Condições da participação';
export const LABEL_VALOR_ORIGINAL = 'Valor original';
export const LABEL_DESCONTO = 'Desconto';
export const LABEL_VALOR_PARTICIPACAO = 'Valor da participação';
export const LABEL_PAGAMENTO = 'Pagamento';
export const LABEL_PARCELA_PREFIXO = 'Parcela';
export const LABEL_VENCIMENTO_PREFIXO = 'venc.';

function paraNumeroSeguro(valor) {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'string' && valor.trim() === '') return null;
  const numero = Number(valor);
  return Number.isNaN(numero) ? null : numero;
}

/** `valor` chega do backend como STRING Decimal (Pydantic v2 serializa
 * Decimal como string em JSON, ex. "170.00") ou, em alguns pontos ja
 * calculados, como number -- aceita os dois identicamente. NUNCA lanca,
 * NUNCA transforma null/undefined/invalido em "R$ 0,00" (isso
 * apresentaria uma condicao comercial aparentemente valida onde na
 * verdade o dado esta ausente/invalido) -- retorna `null` para o
 * chamador decidir omitir a linha inteira. `Number()` e' usado
 * SOMENTE para apresentacao, nunca para decisao financeira. */
export function formatarValorMonetarioPortal(valor) {
  const numero = paraNumeroSeguro(valor);
  if (numero === null) return null;
  return `R$ ${numero.toFixed(2).replace('.', ',')}`;
}

/** `dataIso` chega como "YYYY-MM-DD" (Pydantic `date`) -- mesmo padrao
 * ja usado em todo o projeto (dayjs direto sobre a string, nunca `new
 * Date(...)`, que poderia deslocar o dia em fusos negativos). */
export function formatarDataVencimentoPortal(dataIso) {
  return dataIso ? dayjs(dataIso).format('DD/MM/YYYY') : null;
}

/** Decide se a secao "Condicoes da participacao" deve aparecer.
 * `quantidade_parcelas`/`valor_final` sao os 2 campos estruturalmente
 * obrigatorios juntos (ver CHECK chk_evento_inscricoes_condicoes_
 * completas_quando_aprovada no banco) -- ausencia de qualquer um dos
 * dois e' tratada como "sem condicoes visiveis", nunca renderiza uma
 * secao pela metade. */
export function inscricaoTemCondicoesComerciais(inscricao) {
  if (!inscricao) return false;
  if (paraNumeroSeguro(inscricao.valor_final) === null) return false;
  return inscricao.quantidade_parcelas !== null && inscricao.quantidade_parcelas !== undefined;
}

/** "À vista" para 1 parcela (nunca um campo/flag separado no backend --
 * e' inferido exclusivamente de quantidade_parcelas === 1, mesma
 * semantica confirmada na investigacao E.2), senao "N parcelas".
 * `null` quando quantidade_parcelas e' invalida/ausente -- o chamador
 * so' deveria chegar aqui depois de `inscricaoTemCondicoesComerciais`. */
export function descricaoPagamentoPortal(quantidadeParcelas) {
  const quantidade = paraNumeroSeguro(quantidadeParcelas);
  if (quantidade === null) return null;
  if (quantidade === 1) return 'À vista';
  return `${quantidade} parcelas`;
}

/** Desconto so' e' mostrado quando realmente existe e e' > 0 -- nunca
 * ocupa espaco com "Desconto: R$ 0,00". Nunca calculado aqui, so' usa
 * exatamente `desconto_reais` recebido. */
export function inscricaoTemDescontoVisivel(inscricao) {
  const desconto = paraNumeroSeguro(inscricao?.desconto_reais);
  return desconto !== null && desconto > 0;
}

/** Projeta uma parcela crua da API (numero_parcela/valor/data_vencimento)
 * para os 3 valores ja formatados que o card precisa -- nunca assume
 * parcelas iguais entre si, nunca reordena (a API ja devolve ordenado
 * por numero_parcela). */
export function formatarParcelaPortal(parcela) {
  return {
    numero: parcela?.numero_parcela ?? null,
    valorFormatado: formatarValorMonetarioPortal(parcela?.valor),
    vencimentoFormatado: formatarDataVencimentoPortal(parcela?.data_vencimento),
  };
}
